import { Inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Observable, of, Subscription } from 'rxjs';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import { LocalDate } from '../../../core/model/local-date';
import { PropertyTaxCalculationBasis } from '../../model/const/enums';
import { LeaseRow } from '../../model/lease-row';
import { PropertyTaxRentalChangeModel } from '../../model/property-tax-rental-change';
import { IndexValueService } from './index-value.service';
import { PremisesDraftPropertyTaxCalculationRequest } from './premises-contract.service';

export interface CurrentPeriodRentResponse {
  periodRent: any;
  currentYearRent: any;
}

export interface DraftPropertyTaxRequestSource {
  propertyId: string | null;
  rentalObjectId: string | null;
  leaseAgreementId?: string | null;
  propertyTaxPayer: number | null;
  propertyTaxCalculationBasis: number | null;
  propertyTaxPercentageValue: number | null;
  propertyTaxCalculationMethod: number | null;
  propertyTaxArticleId: string | null;
  propertyTaxRetroactiveArticleId: string | null;
}

export interface PropertyTaxDraftCoordinator {
  bindControlTriggers(form: FormGroup | undefined, controlNames: string[]): void;
  schedule(): void;
  dispose(): void;
}

@Injectable()
export class PropertyTaxCalculationService {
  private readonly _premisesAgreementEndpoint: string;

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _indexValueService: IndexValueService,
    @Inject(APP_CONFIG) config: IAppConfig
  ) {
    this._premisesAgreementEndpoint = `${config.apiEndpointV2}/premises-agreement`;
  }

  calculateDraftPropertyTax(request: PremisesDraftPropertyTaxCalculationRequest): Observable<PropertyTaxRentalChangeModel | null> {
    return this._httpClient.post<PropertyTaxRentalChangeModel | null>(
      `${this._premisesAgreementEndpoint}/draft-property-tax-change`,
      request
    );
  }

  buildDraftPropertyTaxRequest(
    source: DraftPropertyTaxRequestSource,
    tenantValue: number
  ): PremisesDraftPropertyTaxCalculationRequest | null {
    if (source.propertyTaxPayer !== tenantValue) {
      return null;
    }
    if (!source.rentalObjectId || !source.propertyId) {
      return null;
    }
    if (source.propertyTaxCalculationBasis == null || source.propertyTaxCalculationMethod == null || !source.propertyTaxArticleId) {
      return null;
    }
    const propertyTaxPercentage =
      source.propertyTaxCalculationBasis === PropertyTaxCalculationBasis.Percentage && source.propertyTaxPercentageValue != null
        ? source.propertyTaxPercentageValue / 100
        : null;
    if (source.propertyTaxCalculationBasis === PropertyTaxCalculationBasis.Percentage && propertyTaxPercentage == null) {
      return null;
    }
    return {
      propertyId: source.propertyId,
      rentalObjectId: source.rentalObjectId,
      leaseAgreementId: source.leaseAgreementId ?? null,
      propertyTaxPayer: source.propertyTaxPayer,
      propertyTaxCalculationBasis: source.propertyTaxCalculationBasis,
      propertyTaxPercentage,
      propertyTaxCalculationMethod: source.propertyTaxCalculationMethod,
      propertyTaxArticleId: source.propertyTaxArticleId,
      propertyTaxRetroactiveArticleId: source.propertyTaxRetroactiveArticleId,
    };
  }
  normalizeId(value: any): string | null {
    if (typeof value === 'string') {
      return value.trim().length > 0 ? value : null;
    }
    if (value?.id && typeof value.id === 'string') {
      return value.id;
    }
    return null;
  }
  getCurrentPeriodRentForRow(row: LeaseRow): Observable<CurrentPeriodRentResponse | null> {
    if (!row?.yearRent || !row.fromDate?.hasValue()) {
      return of(null);
    }
    const rowFrom = LocalDate.fromDate(row.fromDate).toISOString();
    const rowTo = row.toDate?.hasValue() ? LocalDate.fromDate(row.toDate).toISOString() : 'Empty';
    const calcPeriodFrom = row.currentDuePeriodFrom?.hasValue()
      ? LocalDate.fromDate(row.currentDuePeriodFrom).toISOString()
      : this.getDefaultCurrentDuePeriodFrom().toISOString();
    const calcPeriodTo = row.currentDuePeriodTo?.hasValue()
      ? LocalDate.fromDate(row.currentDuePeriodTo).toISOString()
      : this.getDefaultCurrentDuePeriodTo().toISOString();
    return this._indexValueService.getCurrentPeriodRent(row.yearRent, rowFrom, rowTo, calcPeriodFrom, calcPeriodTo, row.chargedMonths);
  }

  createDraftCoordinator<TRequest, TResponse>(options: {
    createRequest: () => TRequest | null;
    executeRequest: (request: TRequest) => Observable<TResponse | null>;
    onResult: (response: TResponse | null) => void;
    onError: (error: any) => void;
    delayMs?: number;
  }): PropertyTaxDraftCoordinator {
    let watchSub: Subscription | undefined;
    let requestSub: Subscription | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const delayMs = options.delayMs ?? 300;

    const runNow = (): void => {
      requestSub?.unsubscribe();
      const request = options.createRequest();
      if (!request) {
        options.onResult(null);
        return;
      }

      requestSub = options.executeRequest(request).subscribe({
        next: response => options.onResult(response),
        error: error => options.onError(error),
      });
    };

    const schedule = (): void => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = undefined;
        runNow();
      }, delayMs);
    };

    const bindControlTriggers = (form: FormGroup | undefined, controlNames: string[]): void => {
      watchSub?.unsubscribe();
      const sub = new Subscription();

      controlNames.forEach(controlName => {
        const control = form?.get(controlName);
        if (!control) return;
        sub.add(control.valueChanges.subscribe(() => schedule()));
      });

      watchSub = sub;
    };

    const dispose = (): void => {
      watchSub?.unsubscribe();
      requestSub?.unsubscribe();
      if (timer) {
        clearTimeout(timer);
        timer = undefined;
      }
    };

    return { bindControlTriggers, schedule, dispose };
  }

  private getDefaultCurrentDuePeriodFrom(): LocalDate {
    const today = LocalDate.today();
    return new LocalDate(today.year, today.month, 1);
  }
  private getDefaultCurrentDuePeriodTo(): LocalDate {
    const today = LocalDate.today();
    return LocalDate.fromDate(new Date(today.year, today.month, 0));
  }
}
